import React, { useEffect, useState } from 'react'
import { Divider, Grid } from '@mui/material'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { shallowEqual, useSelector } from 'react-redux'
import EntityIndicatorDialog from '../EntityIndicatorDialog'
import { round } from 'lodash'
import IndicatorTable from '../IndicatorTable'
import IndicatorHeaderDialog from '../IndicatorHeaderDialog'
import { D101_0, VP_056, VP_229 } from 'rpqs/constants/RpqsConstant'
import { getIndicatorDialogRow, updateIndicatorEntity, updateManyIndicatorEntity } from 'rpqs/utils/RPQSUtils'

const DialogD101_0 = ({
    open = false,
    setOpen = () => {},
    title = '',
}) => {
    const {
        rpqs,
        managementEntity,
    } = useSelector(store => ({
        rpqs: store.RpqsReducer.rpqs,
        managementEntity: store.CollectivityReducer.managementEntity,
    }), shallowEqual)

    const [entityDialog, setEntity] = useState(managementEntity)
    const [readMode, setReadMode] = useState(true)

    const year = rpqs.year
    const previousYear = year-1
    const currentEntity = entityDialog?.[year]

    const getIndicatorValue = (indicator) => (currentEntity?.[indicator]?.value || 0)

    useEffect(() => {
        if (!readMode) return
        const vp056 = getIndicatorValue('nbNotDomesticSub') + getIndicatorValue('nbDomesticSub')

        const newIndicators = [
            { code: VP_056.code, value: { ...currentEntity?.[VP_056.code], value: vp056 } },
            { code: VP_229.code, value: { ...currentEntity?.[VP_229.code], value: vp056 !== 0 ? round(getIndicatorValue(D101_0.code)/vp056, 2) : getIndicatorValue(VP_229.code) } },
        ]
        setEntity(updateManyIndicatorEntity(entityDialog, year, newIndicators))
    }, [readMode])

    const rows = [
        getIndicatorDialogRow({
            codeIndicator: D101_0.code,
            indicatorName: i18n[D101_0.i18n],
            unit: i18n[D101_0.i18n],
            entityDialog,
            rpqs,
            onChange: v => setEntity(prev => (updateManyIndicatorEntity(prev, year, [
                { code: D101_0.code, value: { ...currentEntity?.[D101_0.code], value: parseInt(v) || 0 } },
                { code: VP_229.code, value: { ...currentEntity?.[VP_229.code], value: (readMode && getIndicatorValue(VP_056.code)) ? round(parseInt(v)/getIndicatorValue(VP_056.code), 2) : getIndicatorValue(VP_229.code) } },
            ]))),
        }),
        getIndicatorDialogRow({
            keyLabel: 'nbDomesticSub',
            indicatorName: i18n.nbDomesticSub,
            unit: i18n.subscriber,
            entityDialog,
            rpqs,
            onChange: v => setEntity(prev => {
                const vp056 = parseInt(v) + getIndicatorValue('nbNotDomesticSub')
                return updateManyIndicatorEntity(prev, year, [
                    { code: 'nbDomesticSub', value: { ...currentEntity?.nbNotDomesticSub, value: parseInt(v) || 0 } },
                    { code: VP_056.code, value: { ...currentEntity?.[VP_056.code], value: readMode ? parseInt(v) + getIndicatorValue('nbNotDomesticSub') : getIndicatorValue(VP_056.code) } },
                    { code: VP_229.code, value: { ...currentEntity?.[VP_056.code], value: (readMode && vp056 != 0 ? round(getIndicatorValue(D101_0.code)/vp056, 2) : getIndicatorValue(VP_229.code)) } },
                ])
            }),
        }),
        getIndicatorDialogRow({
            keyLabel: 'nbNotDomesticSub',
            indicatorName: i18n.nbNotDomesticSub,
            unit: i18n.subscriber,
            entityDialog,
            rpqs,
            onChange: v => setEntity(prev => {
                const vp056 = parseInt(v) + getIndicatorValue('nbDomesticSub')
                return updateManyIndicatorEntity(prev, year, [
                    { code: 'nbNotDomesticSub', value: { ...currentEntity?.nbNotDomesticSub, value: parseInt(v) || 0 } },
                    { code: VP_056.code, value: { ...currentEntity?.[VP_056.code], value: readMode ? parseInt(v) + getIndicatorValue('nbDomesticSub') : getIndicatorValue(VP_056.code) } },
                    { code: VP_229.code, value: { ...currentEntity?.[VP_056.code], value: (readMode && vp056 != 0 ? round(getIndicatorValue(D101_0.code)/vp056, 2) : getIndicatorValue(VP_229.code)) } },
                ])
            }),
        }),
        getIndicatorDialogRow({
            readMode,
            rpqs,
            codeIndicator: VP_056.code,
            indicatorName: i18n[VP_056.i18n],
            unit: i18n.subscriber,
            entityDialog,
            onChange: v => setEntity(prev => (updateIndicatorEntity(prev, year, VP_056.code,
                { ...currentEntity?.[VP_056.code], value: parseInt(v) || 0 },
            ))),
        }),
        getIndicatorDialogRow({
            readMode,
            rpqs,
            codeIndicator: VP_229.code,
            indicatorName: i18n[VP_229.i18n],
            entityDialog,
            onChange: v => setEntity(prev => (updateIndicatorEntity(prev, year, VP_229.code,
                { ...currentEntity?.[VP_229.code], value: parseInt(v) || 0 },
            ))),
        }),
    ]

    const onStatusChange = (status) => {
        setEntity(updateIndicatorEntity(entityDialog, year, D101_0.code, { ...currentEntity?.[D101_0.code], status }))
    }

    const currentValue = managementEntity?.[year]?.[D101_0.code]?.value ? `${managementEntity?.[year]?.[D101_0.code]?.value}` : '/'
    const previousValue = managementEntity?.[previousYear]?.[D101_0.code]?.value ? `${managementEntity?.[previousYear]?.[D101_0.code]?.value}` : '/'

    return (
        <EntityIndicatorDialog
            open={open}
            setOpen={setOpen}
            title={title}
            hasNote
            entityDialog={entityDialog}
            setEntityDialog={setEntity}
            codeIndicator={D101_0.code}
            readMode={readMode}
            setReadMode={setReadMode}
        >
            {year && previousYear &&
                <Grid container direction={'column'} gap={2} minWidth={'fit-content'}>
                    <IndicatorHeaderDialog
                        current={{
                            year,
                            value: currentValue,
                        }}
                        previous={{
                            year: previousYear,
                            value: previousValue,
                        }}
                        status={entityDialog?.[year]?.[D101_0.code]?.status}
                        onStatusChange={onStatusChange}
                        unit={i18n[D101_0.i18n]}
                    />
                    <Divider/>
                    <IndicatorTable
                        year={year.toString()}
                        indicators={rows}
                    />

                </Grid>
            }
        </EntityIndicatorDialog>
    )
}

DialogD101_0.propTypes = {
    setOpen: PropTypes.func,
    open: PropTypes.bool,
    title: PropTypes.string,
}

export default DialogD101_0
